import { createAgxForm, createFormDefinition } from '@urbanx/agx.forms';
import type {
  BrandSettings,
  DesignSystemColourVariable,
  UploadedDocument,
} from '@urbanx/agx.agencies.client';
import { z } from 'zod';

export const colourSchema = z.string().regex(/^#[\da-f]{6}$/i, 'Enter a six-digit hex colour.');
const opacitySchema = z
  .string()
  .regex(/^\d+$/, 'Enter a whole percentage.')
  .refine((value) => Number(value) <= 100, 'Opacity must be between 0 and 100.');

export const brandingSchema = z.object({
  backgroundColor: colourSchema,
  backgroundTextColor: colourSchema,
  accentColorOld: colourSchema,
  buttonTextColor: colourSchema,
  inboxBackgroundColor: colourSchema,
  sidebarBackgroundColor: colourSchema,
  inboxBackgroundColorOpacity: opacitySchema,
  sidebarBackgroundColorOpacity: opacitySchema,
  logo: z.custom<UploadedDocument>().optional(),
  emailLogo: z.custom<UploadedDocument>().optional(),
  brandingColours: z.object({
    mainColour: colourSchema,
    accentColour: colourSchema,
    generatedMainColours: z.custom<DesignSystemColourVariable[]>().optional(),
    generatedAccentColours: z.custom<DesignSystemColourVariable[]>().optional(),
  }),
});

const schema = z
  .object({
    label: z.string().trim().min(1, 'Enter a brand override name.').max(120),
    agencyName: z.string().trim().min(1, 'Enter an agency name.'),
    branding: brandingSchema,
    logoContent: z.string().optional(),
  })
  .superRefine(({ branding, logoContent }, context) => {
    if (!branding.logo && !logoContent)
      context.addIssue({ code: 'custom', path: ['logoContent'], message: 'Upload an SVG logo.' });
  });

const fieldOrder = [
  'label',
  'agencyName',
  'logoContent',
  'branding.backgroundColor',
  'branding.backgroundTextColor',
  'branding.accentColorOld',
  'branding.buttonTextColor',
  'branding.brandingColours.mainColour',
  'branding.brandingColours.accentColour',
  'branding.inboxBackgroundColor',
  'branding.inboxBackgroundColorOpacity',
  'branding.sidebarBackgroundColor',
  'branding.sidebarBackgroundColorOpacity',
] as const;

export const DemoBrandForm = createAgxForm(
  createFormDefinition({
    id: 'demo-brand-editor',
    schema,
    fieldOrder,
    resolveFields: () =>
      Object.fromEntries(
        fieldOrder.map((name) => [
          name,
          { active: true, visible: true, disabled: false, required: true },
        ])
      ),
  })
);

// Match the existing Staff Portal editor's defaults for legacy unset fields.
export function demoBrandDefaults(
  agencyName: string,
  branding: BrandSettings,
  label = ''
): z.input<typeof schema> {
  return {
    label,
    agencyName,
    branding: {
      ...branding,
      backgroundColor: branding.backgroundColor ?? '#FFFFFF',
      backgroundTextColor: branding.backgroundTextColor ?? '#000000',
      accentColorOld: branding.accentColorOld ?? '#000000',
      buttonTextColor: branding.buttonTextColor ?? '#000000',
      inboxBackgroundColor: branding.inboxBackgroundColor ?? '#FFFFFF',
      sidebarBackgroundColor: branding.sidebarBackgroundColor ?? '#FFFFFF',
      inboxBackgroundColorOpacity: branding.inboxBackgroundColorOpacity ?? '0',
      sidebarBackgroundColorOpacity: branding.sidebarBackgroundColorOpacity ?? '0',
      brandingColours: {
        ...branding.brandingColours,
        mainColour: branding.brandingColours?.mainColour ?? '#FFFFFF',
        accentColour: branding.brandingColours?.accentColour ?? '#000000',
      },
    },
  };
}
